import { hashSeed, Rng, type Picture } from "@enjoyment/core";

/**
 * Quiz picture pack loader (apps/arcade/public/pictures/pack-1.json, built by tools/art-factory).
 * Fetched once, cached in memory; the service worker caches it for offline play.
 */
export interface PicturePack {
  version: number;
  themes: string[];
  credits: { source: string; license: string; url: string }[];
  pictures: Picture[];
}

let cached: PicturePack | null = null;
let inflight: Promise<PicturePack> | null = null;

export function loadPictures(): Promise<PicturePack> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetch(`${import.meta.env.BASE_URL}pictures/pack-1.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`pictures: ${r.status}`);
        return r.json() as Promise<PicturePack>;
      })
      .then((p) => {
        cached = p;
        return p;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function picturesLoaded(): PicturePack | null {
  return cached;
}

const hdCache = new Map<string, Promise<HTMLImageElement | null>>();

/**
 * Full-resolution twin of a pack picture (public/pictures/hd/<id>.webp, tools/art-factory/pictures-hd.mjs),
 * for the HD picture experiment. Fetched on demand (never precached); null when missing or offline.
 */
export function loadHdPicture(id: string): Promise<HTMLImageElement | null> {
  let p = hdCache.get(id);
  if (!p) {
    p = new Promise((resolve) => {
      const img = new Image();
      img.decoding = "async";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = `${import.meta.env.BASE_URL}pictures/hd/${encodeURIComponent(id)}.webp`;
    });
    hdCache.set(id, p);
    void p.then((img) => {
      if (!img) hdCache.delete(id); // retry next time (e.g. back online)
    });
  }
  return p;
}

/** Ordered theme list for the menu (known themes first, then anything new in the pack). */
export const THEME_ORDER = ["flags", "animals", "food", "vehicles", "nature", "objects", "music-sport"];

export function orderedThemes(pack: PicturePack): string[] {
  const rest = pack.themes.filter((t) => !THEME_ORDER.includes(t)).sort();
  return [...THEME_ORDER.filter((t) => pack.themes.includes(t)), ...rest];
}

/**
 * Fixed theme sequence: the same shuffled order for every player (keyed by `sequence`, e.g. "v1"),
 * so "round 7 of Bayraklar" is comparable between friends. `theme` = "mixed" uses the whole pack.
 */
export function themeRound(pack: PicturePack, theme: string, sequence: string, index: number): { picture: Picture; seed: number } {
  const pool = theme === "mixed" ? pack.pictures : pack.pictures.filter((p) => p.theme === theme);
  const rng = new Rng(hashSeed(`theme:${theme}:${sequence}`));
  const order = pool.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  const k = index % order.length;
  const picture = pool[order[k]!]!;
  return { picture, seed: hashSeed(`round:${theme}:${sequence}:${k}`) };
}
